import { useHistory } from 'react-router-dom';
import userHaveRolePermission from '../../../../helpers/userHaveRolePermission';
import { PAGE_URLS } from '../../../../helpers/endpoints';
import { useTranslation } from 'react-i18next';
import { Button, ConfigProvider, Table, Tag } from 'antd';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  debouncedDataLoadWaitMs,
  ORDER_TYPES,
} from '../../../../helpers/constants';
import debounce from 'lodash/debounce';
import trTR from 'antd/es/locale/tr_TR';
import {
  getBulkDefinitionsTraces,
  getDefinitionTrace,
} from '../../../../store/requests/definitions';
import { CSVLink } from 'react-csv';

const BulkDefinitionTracing = ({ route }) => {
  const history = useHistory();
  const { isViewEnabled, isCrudEnabled } = userHaveRolePermission(route?.name);
  if (!isViewEnabled) {
    history.push(`${PAGE_URLS.AUTH}${PAGE_URLS.CONTROL}`);
  }
  const csvLinkRef = useRef();
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [csvData, setCsvData] = useState({ csvName: null, data: [] });
  const [loading, setLoading] = useState(false);
  const [cvsLoadingIndex, setCvsLoadingIndex] = useState(null);
  const [tableParams, setTableParams] = useState({
    pagination: {
      showSizeChanger: true,
      showQuickJumper: true,
      showTotal: (total) => `Toplam ${total} kayıt`,
      pageSizeOptions: [20, 50, 100],
      size: 'small',
      current: 1,
      pageSize: 20,
    },
  });

  useEffect(() => {
    fetchData();
  }, [JSON.stringify(tableParams)]);

  const fetchData = () => {
    const queryString = getQueryString();
    loadData(queryString);
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setTableParams({
      pagination,
      ...sorter,
    });
  };

  const getQueryString = () => {
    let queryString = '';
    queryString = `${queryString}&page=${tableParams.pagination.current - 1}&size=${tableParams.pagination.pageSize}`;
    if (tableParams.column) {
      queryString = `${queryString}&sort=${tableParams.column.dataIndex},${ORDER_TYPES[tableParams.order]}`;
    } else {
      queryString = `${queryString}&sort=id,desc`;
    }
    return queryString;
  };

  async function loadDataWithDebounce(queryString) {
    if (!loading) {
      setLoading(true);
      getBulkDefinitionsTraces(queryString)
        .then((response) => {
          const { content, page } = response;
          setRows(content ?? []);
          setTableParams({
            ...tableParams,
            pagination: {
              ...tableParams.pagination,
              total: page.totalElements,
            },
          });
        })
        .finally(() => setLoading(false));
    }
  }

  const loadData = useCallback(
    debounce((queryString) => {
      loadDataWithDebounce(queryString);
    }, debouncedDataLoadWaitMs),
  );

  const columns = [
    {
      title: t('actionID'),
      dataIndex: 'id',
      sorter: true,
      showSorterTooltip: false,
      key: '1',
      width: 100,
    },
    {
      title: t('action'),
      dataIndex: 'type',
      sorter: true,
      showSorterTooltip: false,
      width: 200,
      key: '2',
    },
    {
      title: t('loadingTime'),
      dataIndex: 'startDate',
      sorter: true,
      showSorterTooltip: false,
      key: '3',
      render: (text, record) =>
        new Date(text)
          ?.toLocaleDateString('tr-TR', {
            hour: '2-digit',
            minute: '2-digit',
          })
          .toString(),
    },
    {
      title: t('routeEndTime'),
      dataIndex: 'finishDate',
      sorter: true,
      showSorterTooltip: false,
      key: '4',
      render: (text, record) =>
        new Date(text)
          ?.toLocaleDateString('tr-TR', {
            hour: '2-digit',
            minute: '2-digit',
          })
          .toString(),
    },
    {
      title: 'Yükleyen',
      dataIndex: 'importerUserName',
      sorter: true,
      showSorterTooltip: false,
      key: '5',
      render: (text, record) => text ?? '-',
    },
    {
      title: t('status'),
      dataIndex: 'status',
      sorter: true,
      showSorterTooltip: false,
      key: '6',
      render: (text, record) => t(text),
    },
    {
      title: t('numberOfSuccessfulRegistrations'),
      dataIndex: 'successProcessCount',
      sorter: true,
      showSorterTooltip: false,
      key: '7',
    },
    {
      title: t('totalNumberOfRecords'),
      dataIndex: 'totalProcessCount',
      sorter: true,
      showSorterTooltip: false,
      key: '8',
    },
    {
      title: t('file'),
      dataIndex: 'fileName',
      sorter: true,
      fixed: 'right',
      width: 400,
      showSorterTooltip: false,
      render: (text, data, index) =>
        data.successProcessCount < data.totalProcessCount ? (
          <div>
            <Button
              type="link"
              onClick={getDefinitionTraceExcelInfo(data, index)}
              danger={data.successProcessCount < data.totalProcessCount}
              loading={index === cvsLoadingIndex}
            >
              {text}
            </Button>
            <CSVLink
              filename={csvData.csvName}
              data={csvData.data}
              ref={csvLinkRef}
              className="hidden"
            />
          </div>
        ) : (
          <Tag style={{ marginLeft: 20 }} color="success">
            {t('success')}
          </Tag>
        ),
      key: '9',
    },
  ];

  const getDefinitionTraceExcelInfo = (trace, index) => () => {
    setCvsLoadingIndex(index);
    getDefinitionTrace(trace.id)
      .then((response) => {
        if (response.content.length > 0) {
          const columnNames = Array.from(
            new Set([
              'Satır Numarası',
              ...response?.content.flatMap((entry) =>
                entry.fieldValues.map((field) => field.name),
              ),
            ]),
          );
          const data = [columnNames];

          response?.content.forEach((entry) => {
            const row = [entry.rowNumber];
            columnNames.slice(1).forEach((column) => {
              const field = entry.fieldValues.find(
                (field) => field.name === column,
              );
              row.push(field ? field.value : '');
            });
            data.push(row);
          });
          setCsvData({ csvName: trace.fileName.split('.xlsx')[0], data });
          csvLinkRef.current.link.click();
        }
      })
      .finally(() => {
        setCvsLoadingIndex(null);
      });
  };
  return (
    <ConfigProvider locale={trTR}>
      <Table
        style={{ width: '100%' }}
        size="small"
        columns={columns}
        dataSource={rows}
        pagination={tableParams.pagination}
        loading={loading}
        onChange={handleTableChange}
        rowKey={(record) => record.id}
        scroll={{
          y: '70vh',
          x: 1300,
        }}
      />
    </ConfigProvider>
  );
};

export default BulkDefinitionTracing;
